// Owner: S10. DTOs and input schemas for identity, access & administration.
// Import as `@dataroom/core/access`.
import { z } from 'zod';
import { HexColor } from './colors';
import { FilterNodeSchema, type FilterNode } from './filter';
import { DateFormat, NumberFormat, TimeZone } from './session';

/** Permissions added by S10 (next to PERMISSIONS in session.ts). */
export const ACCESS_PERMISSIONS = {
  /** View the administration screens (members, roles, security, audit, general). */
  settingsRead: 'settings.read',
  /** Invite, deactivate and change roles/teams of members (not into roles with workspace.admin). */
  membersManage: 'members.manage',
  /** Read the audit log. */
  auditRead: 'audit.read',
} as const;

/**
 * Permissions a role can grant in Settings › Roles, in display order. Full administration
 * (`workspace.admin`) is not grantable: it stays with the system admin roles.
 */
export const GRANTABLE_PERMISSIONS = [
  'records.read',
  'records.write',
  'records.delete',
  'records.export',
  'fields.decrypt',
  'dashboards.create',
  'metadata.write',
  'integrations.manage',
  'settings.read',
  'settings.manage',
  'members.manage',
  'audit.read',
] as const;
export const GrantablePermission = z.enum(GRANTABLE_PERMISSIONS);
export type GrantablePermission = z.infer<typeof GrantablePermission>;

export const AccessLevel = z.enum(['all', 'team', 'own', 'none']);
export type AccessLevel = z.infer<typeof AccessLevel>;

export const ObjectAccessInput = z.object({
  objectKey: z.string().min(1).max(63),
  view: AccessLevel,
  create: z.boolean(),
  edit: AccessLevel,
  delete: AccessLevel,
  export: z.boolean(),
});
export type ObjectAccessInput = z.infer<typeof ObjectAccessInput>;

export const ObjectAccessDTO = ObjectAccessInput.extend({
  objectLabel: z.string(),
  appLabel: z.string().nullable(),
  color: z.string(),
  /** No explicit row: derived from the role's coarse permissions. */
  isDefault: z.boolean(),
});
export type ObjectAccessDTO = z.infer<typeof ObjectAccessDTO>;

export const FieldAccess = z.enum(['hidden', 'read_only']);
export type FieldAccess = z.infer<typeof FieldAccess>;

export const FieldRestrictionInput = z.object({
  objectKey: z.string().min(1).max(63),
  fieldKey: z.string().min(1).max(63),
  access: FieldAccess,
});
export type FieldRestrictionInput = z.infer<typeof FieldRestrictionInput>;

export const FieldRestrictionDTO = FieldRestrictionInput.extend({
  id: z.string(),
  objectLabel: z.string(),
  fieldLabel: z.string(),
});
export type FieldRestrictionDTO = z.infer<typeof FieldRestrictionDTO>;

export const MiniMember = z.object({
  id: z.string(),
  name: z.string(),
  initials: z.string(),
  avatarColor: HexColor,
});
export type MiniMember = z.infer<typeof MiniMember>;

export const RoleKind = z.enum(['member', 'service']);
export type RoleKind = z.infer<typeof RoleKind>;

export const RoleSummaryDTO = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  kind: RoleKind,
  isSystem: z.boolean(),
  isAdmin: z.boolean(),
  memberCount: z.number().int(),
  apiKeyCount: z.number().int(),
  sampleMembers: z.array(MiniMember),
  directoryGroups: z.array(z.string()),
  permissions: z.array(z.string()),
});
export type RoleSummaryDTO = z.infer<typeof RoleSummaryDTO>;

export const RoleDetailDTO = RoleSummaryDTO.extend({
  access: z.array(ObjectAccessDTO),
  fieldRestrictions: z.array(FieldRestrictionDTO),
});
export type RoleDetailDTO = z.infer<typeof RoleDetailDTO>;

export const RoleLabel = z.string().trim().min(1).max(80);

export const CreateRoleInput = z.object({
  label: RoleLabel,
  description: z.string().trim().max(300).nullish(),
  /** Copy access matrix, restrictions and permissions from this role. */
  copyFromRoleId: z.uuid().optional(),
});
export type CreateRoleInput = z.infer<typeof CreateRoleInput>;

export const SaveRoleInput = z.object({
  label: RoleLabel.optional(),
  description: z.string().trim().max(300).nullish(),
  access: z.array(ObjectAccessInput).max(500).optional(),
  fieldRestrictions: z.array(FieldRestrictionInput).max(1000).optional(),
  /** The role's grantable permissions (replaces them; other stored permissions are kept). */
  permissions: z.array(GrantablePermission).max(GRANTABLE_PERMISSIONS.length).optional(),
});
export type SaveRoleInput = z.infer<typeof SaveRoleInput>;

export const PolicyScope = z.enum(['own', 'team']);
export type PolicyScope = z.infer<typeof PolicyScope>;

export const RecordPolicyDTO = z.object({
  objectKey: z.string(),
  objectLabel: z.string(),
  scope: PolicyScope,
  filter: FilterNodeSchema,
  isDefault: z.boolean(),
});
export type RecordPolicyDTO = Omit<z.infer<typeof RecordPolicyDTO>, 'filter'> & { filter: FilterNode };

export const SaveRecordPolicyInput = z.object({ filter: FilterNodeSchema });

/** `none`: active, but no local password yet (sign-in needs an invite link or an admin set-up). */
export const SignInMethod = z.enum(['sso', 'password_2fa', 'password', 'pending', 'none']);
export type SignInMethod = z.infer<typeof SignInMethod>;

export const MEMBER_SOURCES = ['setup', 'manual', 'invite', 'directory', 'scim'] as const;
export const MemberSource = z.enum(MEMBER_SOURCES);
export type MemberSource = z.infer<typeof MemberSource>;

/** The member source of a `members.provisioned_by` value (null: created when the workspace was set up). */
export function memberSourceOf(provisionedBy: string | null): MemberSource {
  return MemberSource.safeParse(provisionedBy).success ? (provisionedBy as MemberSource) : 'setup';
}

export const MemberAdminDTO = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  avatarColor: HexColor,
  title: z.string().nullable(),
  roleId: z.string(),
  roleKey: z.string(),
  roleLabel: z.string(),
  status: z.enum(['active', 'invited', 'deactivated']),
  teams: z.array(z.object({ id: z.string(), name: z.string() })),
  signIn: SignInMethod,
  lastActiveAt: z.string().nullable(),
  provisionedBy: z.string().nullable(),
  /**
   * Where the member came from: created when the workspace was set up, added by an admin, invited by
   * e-mail, added from the directory of the connected Microsoft 365 / Google Workspace tenant, or SCIM.
   */
  source: MemberSource.default('setup'),
  externalId: z.string().nullable(),
  invitedAt: z.string().nullable(),
  isSelf: z.boolean(),
  /** Custom member fields (members-extensible): field api key → API value; only readable fields. */
  fields: z.record(z.string(), z.unknown()).optional(),
});
export type MemberAdminDTO = z.infer<typeof MemberAdminDTO>;

export const MemberStatusFilter = z.enum(['all', 'active', 'invited', 'deactivated']);

export const ListMembersQuery = z.object({
  q: z.string().trim().max(200).optional(),
  roleId: z.uuid().optional(),
  teamId: z.uuid().optional(),
  status: MemberStatusFilter.default('all'),
  /** Filter on custom member fields: a filter AST (JSON) over the fields of the `member` object. */
  filter: z.string().max(20_000).optional(),
  /** Order by a custom member field (api key); the default order is "joined". */
  sortField: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(63)
    .optional(),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
export type ListMembersQuery = z.input<typeof ListMembersQuery>;

export const MembersListDTO = z.object({
  items: z.array(MemberAdminDTO),
  total: z.number().int(),
  counts: z.object({
    members: z.number().int(),
    invites: z.number().int(),
    roles: z.number().int(),
    teams: z.number().int(),
  }),
  seats: z.object({ used: z.number().int(), total: z.number().int().nullable() }),
  scim: z.object({
    enabled: z.boolean(),
    providerName: z.string().nullable(),
    lastSyncAt: z.string().nullable(),
  }),
  /**
   * The directory of the connected Microsoft 365 / Google Workspace tenant people are added from (null: no
   * connection). `on`: the connection reads the directory (Settings › People › Directory).
   */
  directory: z
    .object({
      provider: z.enum(['microsoft', 'google']),
      connectionName: z.string(),
      on: z.boolean(),
      syncedAt: z.string().nullable(),
      failing: z.boolean(),
      /** Members linked to a person of the directory. */
      linked: z.number().int(),
    })
    .nullable()
    .default(null),
});
export type MembersListDTO = z.infer<typeof MembersListDTO>;

export const InviteMembersInput = z.object({
  emails: z.array(z.email().max(254)).min(1).max(50),
  roleId: z.uuid(),
  teamIds: z.array(z.uuid()).max(50).default([]),
});
export type InviteMembersInput = z.input<typeof InviteMembersInput>;

export const InviteResultDTO = z.object({
  invited: z.array(z.object({ memberId: z.string(), email: z.string(), inviteUrl: z.string() })),
  skipped: z.array(z.object({ email: z.string(), reason: z.string() })),
});
export type InviteResultDTO = z.infer<typeof InviteResultDTO>;

export const UpdateMemberInput = z
  .object({
    roleId: z.uuid().optional(),
    /** Optimistic guard for role changes and their Undo. */
    expectedRoleId: z.uuid().optional(),
    teamIds: z.array(z.uuid()).max(100).optional(),
    status: z.enum(['active', 'deactivated']).optional(),
    title: z.string().trim().max(120).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateMemberInput = z.infer<typeof UpdateMemberInput>;

export const BreakGlassDTO = z.object({
  memberId: z.string(),
  password: z.string(),
  totpSecret: z.string(),
  otpauthUrl: z.string(),
});
export type BreakGlassDTO = z.infer<typeof BreakGlassDTO>;

export const TeamDTO = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  memberCount: z.number().int(),
  members: z.array(MiniMember),
  externalId: z.string().nullable(),
});
export type TeamDTO = z.infer<typeof TeamDTO>;

export const TeamInput = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullish(),
  memberIds: z.array(z.uuid()).max(5000).optional(),
});
export type TeamInput = z.infer<typeof TeamInput>;

export const ViewAsDTO = z.object({
  member: MiniMember.extend({ email: z.string(), roleLabel: z.string(), teams: z.array(z.string()) }),
  objects: z.array(
    z.object({
      objectKey: z.string(),
      label: z.string(),
      color: z.string(),
      view: AccessLevel,
      create: z.boolean(),
      edit: AccessLevel,
      delete: AccessLevel,
      export: z.boolean(),
      visibleCount: z.number().int(),
      totalCount: z.number().int(),
      hiddenFields: z.array(z.string()),
      readOnlyFields: z.array(z.string()),
    }),
  ),
});
export type ViewAsDTO = z.infer<typeof ViewAsDTO>;

/** "Test access" (Roles › View as): may a member view, edit or delete one record, and why. */
export const AccessExplainInput = z.object({
  memberId: z.uuid(),
  objectKey: z.string().min(1).max(63),
  recordId: z.uuid(),
});
export type AccessExplainInput = z.infer<typeof AccessExplainInput>;

/**
 * Why an action is allowed or not: `admin` (full access), `all` / `none` (the role's level),
 * `own` / `team` (the record matches the Own / Team rule) and `not_own` / `not_team` (it does not).
 */
export const AccessExplainReason = z.enum(['admin', 'all', 'none', 'own', 'team', 'not_own', 'not_team']);
export type AccessExplainReason = z.infer<typeof AccessExplainReason>;

export const AccessExplainDTO = z.object({
  member: MiniMember.extend({ roleLabel: z.string(), teams: z.array(z.string()) }),
  record: z.object({ id: z.string(), title: z.string(), objectKey: z.string(), objectLabel: z.string() }),
  /** The field the default Own / Team rules compare (owner, else "Created by") and who it names. */
  owner: z.object({ fieldLabel: z.string(), names: z.array(z.string()) }).nullable(),
  admin: z.boolean(),
  actions: z.array(
    z.object({
      action: z.enum(['view', 'edit', 'delete']),
      level: AccessLevel,
      allowed: z.boolean(),
      reason: AccessExplainReason,
      /** The Own / Team level uses a custom record rule instead of the owner field. */
      customRule: z.boolean(),
    }),
  ),
  create: z.boolean(),
  export: z.boolean(),
  /** Field labels the member's role hides / shows read-only on this object. */
  hiddenFields: z.array(z.string()),
  readOnlyFields: z.array(z.string()),
});
export type AccessExplainDTO = z.infer<typeof AccessExplainDTO>;

/** Effective permissions of the caller per object (used by list screens to show/hide actions). */
export const EffectiveAccessDTO = z.object({
  isAdmin: z.boolean(),
  permissions: z.array(z.string()),
  objects: z.record(
    z.string(),
    z.object({
      view: AccessLevel,
      create: z.boolean(),
      edit: AccessLevel,
      delete: AccessLevel,
      export: z.boolean(),
      /** Field keys the caller's role may not see / may not change. */
      hiddenFields: z.array(z.string()),
      readOnlyFields: z.array(z.string()),
    }),
  ),
});
export type EffectiveAccessDTO = z.infer<typeof EffectiveAccessDTO>;

export const EditableRecordsInput = z.object({ ids: z.array(z.uuid()).max(500) });
export type EditableRecordsInput = z.infer<typeof EditableRecordsInput>;
export const EditableRecordsDTO = z.object({ ids: z.array(z.string()) });
export type EditableRecordsDTO = z.infer<typeof EditableRecordsDTO>;

// -- security ---------------------------------------------------------------------------------

const Cidr = z
  .string()
  .trim()
  .regex(/^(\d{1,3}\.){3}\d{1,3}(\/(3[0-2]|[12]?\d))?$|^[0-9a-f:]+(\/\d{1,3})?$/i, {
    error: 'Expected an IPv4/IPv6 address or CIDR range',
  });
const Domain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, { error: 'Expected a domain' });

export const SecuritySettings = z.object({
  requireSso: z.boolean(),
  allowedDomains: z.array(Domain).max(50),
  sessionHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 90),
  idleMinutes: z
    .number()
    .int()
    .min(5)
    .max(24 * 60 * 7),
  twoFactorRequired: z.boolean(),
  ipAllowlistEnabled: z.boolean(),
  ipAllowlist: z.array(Cidr).max(200),
  auditRetentionDays: z.number().int().min(30).max(3650),
  deletedRetentionDays: z.number().int().min(1).max(365),
  encryptedFieldIds: z.array(z.string()).max(500),
});
export type SecuritySettings = z.infer<typeof SecuritySettings>;
export const UpdateSecuritySettings = SecuritySettings.partial();

export const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
  requireSso: false,
  allowedDomains: [],
  sessionHours: 12,
  idleMinutes: 60,
  twoFactorRequired: true,
  ipAllowlistEnabled: false,
  ipAllowlist: [],
  auditRetentionDays: 730,
  deletedRetentionDays: 30,
  encryptedFieldIds: [],
};

export const IdentityProviderDTO = z.object({
  id: z.string(),
  kind: z.literal('oidc'),
  name: z.string(),
  issuer: z.string(),
  clientId: z.string(),
  hasSecret: z.boolean(),
  scopes: z.string(),
  enabled: z.boolean(),
  lastTestedAt: z.string().nullable(),
  lastTestOk: z.boolean().nullable(),
  lastTestError: z.string().nullable(),
  callbackUrl: z.string(),
  /**
   * Registered by a connected Microsoft 365 / Google Workspace tenant ("Sign in with Microsoft"): set up with
   * that connection's switch on Settings › Sign-in, not edited as a provider of its own. Null: added by hand.
   */
  tenant: z
    .object({
      provider: z.enum(['microsoft', 'google']),
      connectionId: z.string(),
      connectionName: z.string(),
    })
    .nullable()
    .default(null),
});
export type IdentityProviderDTO = z.infer<typeof IdentityProviderDTO>;

export const IdentityProviderInput = z.object({
  name: z.string().trim().min(1).max(80),
  issuer: z.url({ protocol: /^https?$/ }).max(500),
  clientId: z.string().trim().min(1).max(300),
  /** Omit to keep the stored secret; empty string removes it (public client with PKCE). */
  clientSecret: z.string().max(2000).optional(),
  scopes: z
    .string()
    .trim()
    .max(300)
    .regex(/^[\w:./-]+( [\w:./-]+)*$/)
    .default('openid email profile'),
  enabled: z.boolean().default(true),
});
export type IdentityProviderInput = z.input<typeof IdentityProviderInput>;

export const ScimTokenDTO = z.object({
  id: z.string(),
  label: z.string(),
  hint: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ScimTokenDTO = z.infer<typeof ScimTokenDTO>;

export const GroupMappingDTO = z.object({
  id: z.string(),
  group: z.string(),
  roleId: z.string(),
  roleLabel: z.string(),
});
export type GroupMappingDTO = z.infer<typeof GroupMappingDTO>;

export const GroupMappingsInput = z.object({
  mappings: z.array(z.object({ group: z.string().trim().min(1).max(200), roleId: z.uuid() })).max(200),
});

export const PrivacyRequestDTO = z.object({
  id: z.string(),
  kind: z.enum(['export', 'erase']),
  subjectEmail: z.string(),
  subjectName: z.string().nullable(),
  status: z.enum(['open', 'completed', 'rejected']),
  note: z.string().nullable(),
  requestedBy: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  matches: z.number().int().nullable(),
});
export type PrivacyRequestDTO = z.infer<typeof PrivacyRequestDTO>;

export const PrivacyRequestInput = z.object({
  kind: z.enum(['export', 'erase']),
  subjectEmail: z.email().max(254),
  subjectName: z.string().trim().max(200).nullish(),
  note: z.string().trim().max(1000).nullish(),
});
export type PrivacyRequestInput = z.infer<typeof PrivacyRequestInput>;

export const SecurityDTO = z.object({
  /**
   * Single sign-on with an OpenID Connect provider is available (in scope since the tenant-connect
   * decision; docs/SCOPE-v1.md §2 lists the rest as deferred). False: the screen shows the SSO card as not configured.
   */
  externalIdentity: z.boolean(),
  /** SCIM provisioning is available (still deferred in v1: only with `DR_EXTERNAL_CONNECTIONS=1`). */
  scimAvailable: z.boolean().default(false),
  settings: SecuritySettings,
  providers: z.array(IdentityProviderDTO),
  scim: z.object({
    endpoint: z.string(),
    tokens: z.array(ScimTokenDTO),
    lastSyncAt: z.string().nullable(),
    userCount: z.number().int(),
    groupMappings: z.array(GroupMappingDTO),
  }),
  keyring: z.object({
    activeVersion: z.number().int(),
    provider: z.string(),
    rotatedAt: z.string().nullable(),
  }),
  encryptedFields: z.array(z.object({ fieldId: z.string(), label: z.string() })),
  privacy: z.object({ open: z.number().int() }),
  breakGlassAccounts: z.number().int(),
  /** The address this request came from, as the server sees it (IP allowlist lockout guard). */
  callerIp: z.string().nullable().optional(),
});
export type SecurityDTO = z.infer<typeof SecurityDTO>;

// -- general ----------------------------------------------------------------------------------

export * from './workspace-modules';

/**
 * Stored as the `general` setting. Keys earlier versions stored are not part of it and parsing drops
 * them (the object is not strict): `exchangeRates` (a rate source nothing fetched) and `logo` (the
 * workspace logo lives in the brand kit; the `general-logo-to-brand-kit` data migration moves it).
 */
export const GeneralSettings = z.object({
  address: z.string().trim().max(253),
  /** ISO 4217 codes besides the base currency. */
  otherCurrencies: z.array(z.string().regex(/^[A-Z]{3}$/)).max(30),
  fiscalYearStart: z.number().int().min(1).max(12),
  numberFormat: NumberFormat,
  dateFormat: DateFormat,
});
export type GeneralSettings = z.infer<typeof GeneralSettings>;

export const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  address: '',
  otherCurrencies: [],
  fiscalYearStart: 1,
  numberFormat: 'de',
  dateFormat: 'dmy_dot',
};

export const UpdateGeneralInput = GeneralSettings.partial().extend({
  name: z.string().trim().min(1).max(120).optional(),
  locale: z.enum(['en', 'de']).optional(),
  timezone: TimeZone.optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
});
export type UpdateGeneralInput = z.infer<typeof UpdateGeneralInput>;

export const LicenseDTO = z.object({
  edition: z.string(),
  validUntil: z.string(),
  instance: z.string(),
  deployment: z.string(),
  seats: z.number().int(),
  status: z.enum(['valid', 'expiring', 'expired']),
});
export type LicenseDTO = z.infer<typeof LicenseDTO>;

/** A license key pasted or dropped on General › License. */
export const LicenseKeyInput = z.object({ key: z.string().trim().min(1).max(8000) });
export type LicenseKeyInput = z.infer<typeof LicenseKeyInput>;

/**
 * What a key would apply, before applying it. `problem` (when `ok` is false) is a code the client
 * words: `format` (not a DR1 key), `damaged` (unreadable content), `signature` (not signed by the
 * vendor), `expired`, `managed` (the installation's license comes from DR_LICENSE_KEY).
 */
export const LicensePreviewDTO = z.object({
  ok: z.boolean(),
  problem: z.enum(['format', 'damaged', 'signature', 'expired', 'managed']).nullable(),
  licensee: z.string().nullable(),
  edition: z.string().nullable(),
  seats: z.number().int().nullable(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  status: z.enum(['valid', 'expiring', 'expired']).nullable(),
  development: z.boolean(),
  /** Seats in use now (active and invited members); a key with fewer seats is flagged. */
  seatsUsed: z.number().int(),
  /** The license it replaces, when one is applied. */
  current: z.object({ edition: z.string(), seats: z.number().int(), validUntil: z.string() }).nullable(),
});
export type LicensePreviewDTO = z.infer<typeof LicensePreviewDTO>;

export const UpdateInfoDTO = z.object({
  /** Update UI is shown only when the instance has a configured channel. */
  channel: z.string().nullable().optional(),
  current: z.string(),
  available: z.string().nullable(),
  migrations: z.number().int(),
  onlineMigrations: z.boolean(),
  preflight: z.object({
    status: z.enum(['passed', 'failed', 'not_run']),
    checkedAt: z.string().nullable(),
    checks: z.array(z.object({ label: z.string(), ok: z.boolean(), detail: z.string() })),
  }),
  changelog: z.array(z.object({ title: z.string(), items: z.array(z.string()) })),
});
export type UpdateInfoDTO = z.infer<typeof UpdateInfoDTO>;

export const GeneralDTO = z.object({
  workspace: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    locale: z.string(),
    timezone: z.string(),
    currency: z.string(),
  }),
  settings: GeneralSettings,
  deletion: z
    .object({ scheduledFor: z.string(), requestedAt: z.string(), requestedBy: z.string().nullable() })
    .nullable(),
  updatedAt: z.string().nullable(),
});
export type GeneralDTO = z.infer<typeof GeneralDTO>;

/**
 * Settings › Installation (instance operators only): the license and the updates belong to the
 * installation, not to one workspace. `seats.used` counts the active and invited members of the
 * caller's workspace, as inviting does.
 */
export const InstanceDTO = z.object({
  license: LicenseDTO.nullable(),
  seats: z.object({ used: z.number().int(), total: z.number().int().nullable() }),
  updates: UpdateInfoDTO,
});
export type InstanceDTO = z.infer<typeof InstanceDTO>;

/**
 * Who runs the installation: the administrators of the workspace it was set up with (the operator workspace).
 * Settings › Installation and the other installation pages name them to an administrator who is not one.
 */
export const InstallationOperatorsDTO = z.object({
  workspace: z.string().nullable(),
  operators: z.array(
    z.object({ name: z.string(), email: z.string(), initials: z.string(), color: z.string() }),
  ),
});
export type InstallationOperatorsDTO = z.infer<typeof InstallationOperatorsDTO>;

// -- audit ------------------------------------------------------------------------------------

export const AuditVia = z.enum(['Web', 'API', 'Automation', 'Connector', 'Import', 'SSO', 'System', 'SCIM']);
export type AuditVia = z.infer<typeof AuditVia>;

/**
 * One changed value. `field`, `from` and `to` are the readable text written at the time (English
 * fallbacks, `empty` for no value). Record changes since admin-ops also carry the field `key` and the
 * stored values (`fromValue`/`toValue`, absent when sealed or too large) so the audit log renders them
 * typed, in the reader's language, with the field's current label.
 */
export const AuditChange = z.object({
  field: z.string(),
  from: z.string(),
  to: z.string(),
  key: z.string().optional(),
  fromValue: z.unknown().optional(),
  toValue: z.unknown().optional(),
  sealed: z.boolean().optional(),
});
export type AuditChange = z.infer<typeof AuditChange>;

export const AuditEventDTO = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actor: z.object({
    type: z.string(),
    id: z.string().nullable(),
    label: z.string(),
    initials: z.string(),
    color: z.string(),
    fg: z.string(),
  }),
  action: z.string(),
  category: z.string(),
  objectKey: z.string().nullable(),
  target: z.object({ type: z.string().nullable(), id: z.string().nullable(), label: z.string().nullable() }),
  summary: z.string().nullable(),
  changes: z.array(AuditChange),
  via: z.string(),
  itemCount: z.number().int(),
  correlationId: z.string().nullable(),
  causationId: z.string().nullable(),
});
export type AuditEventDTO = z.infer<typeof AuditEventDTO>;

export const AuditEventDetailDTO = AuditEventDTO.extend({
  context: z.object({
    client: z.string().nullable(),
    ip: z.string().nullable(),
    session: z.string().nullable(),
    permission: z.string().nullable(),
  }),
  recordVersion: z.number().int().nullable(),
  meta: z.record(z.string(), z.unknown()),
  /** Every event of the same correlation id (oldest first, at most 30), this one included. */
  chain: z.array(AuditEventDTO),
  next: z.array(
    z.object({
      kind: z.enum(['workflow_run', 'event']),
      id: z.string(),
      title: z.string(),
      subtitle: z.string(),
      href: z.string().nullable(),
      status: z.string().nullable(),
    }),
  ),
});
export type AuditEventDetailDTO = z.infer<typeof AuditEventDetailDTO>;

export const AuditQuery = z.object({
  q: z.string().trim().max(200).optional(),
  /** Actor id, or `named:<type>:<label>` for actors without one (forms, imports, system). */
  actor: z.string().max(300).optional(),
  category: z.string().max(40).optional(),
  object: z.string().max(63).optional(),
  /** Events of one correlation id (a whole chain: the change and everything it caused). */
  correlation: z.uuid().optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type AuditQuery = z.input<typeof AuditQuery>;

export const AuditPageDTO = z.object({ items: z.array(AuditEventDTO), nextCursor: z.string().nullable() });
export type AuditPageDTO = z.infer<typeof AuditPageDTO>;

export const AuditFacetsDTO = z.object({
  actors: z.array(z.object({ id: z.string(), label: z.string(), type: z.string() })),
  categories: z.array(z.object({ key: z.string(), label: z.string() })),
  objects: z.array(z.object({ key: z.string(), label: z.string() })),
  retentionDays: z.number().int(),
});
export type AuditFacetsDTO = z.infer<typeof AuditFacetsDTO>;

/** The filter part of an audit query that a saved filter keeps (the time window is relative). */
export const AuditSavedQuery = z.object({
  q: z.string().trim().max(200).optional(),
  actor: z.string().max(300).optional(),
  category: z.string().max(40).optional(),
  object: z.string().max(63).optional(),
  when: z.enum(['24h', '7d', '30d', '90d', 'all']).optional(),
});
export type AuditSavedQuery = z.infer<typeof AuditSavedQuery>;

/** A named audit filter shared by everyone who can read the audit log. */
export const AuditSavedFilterDTO = z.object({
  id: z.string(),
  name: z.string(),
  query: AuditSavedQuery,
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditSavedFilterDTO = z.infer<typeof AuditSavedFilterDTO>;

export const SaveAuditFilterInput = z.object({
  name: z.string().trim().min(1).max(80),
  query: AuditSavedQuery,
});
export type SaveAuditFilterInput = z.infer<typeof SaveAuditFilterInput>;

export const AuditStreamSettings = z.object({
  enabled: z.boolean(),
  url: z.url({ protocol: /^https?$/ }).max(500),
  format: z.enum(['json', 'cef']),
  /** Write-only bearer token; omitted in reads. */
  token: z.string().max(2000).optional(),
});
export type AuditStreamSettings = z.infer<typeof AuditStreamSettings>;

export const AuditStreamDTO = z.object({
  enabled: z.boolean(),
  url: z.string().nullable(),
  format: z.enum(['json', 'cef']),
  hasToken: z.boolean(),
  lastDeliveredAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type AuditStreamDTO = z.infer<typeof AuditStreamDTO>;

// -- sign-in ----------------------------------------------------------------------------------

export const AuthProvidersDTO = z.object({
  workspace: z.object({
    name: z.string(),
    slug: z.string(),
    address: z.string().nullable(),
    /**
     * The public key of the workspace's brand kit (`getPublicBrand`): the sign-in page shows its logo
     * and accent. Null or absent while the workspace has no brand kit.
     */
    brandKey: z.string().nullable().optional(),
  }),
  providers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      kind: z.literal('oidc'),
      /** The well-known identity provider behind it (read from the issuer), for its mark on the button; null for any other. */
      brand: z.enum(['microsoft', 'google']).nullable().optional(),
    }),
  ),
  requireSso: z.boolean(),
  devLogin: z.boolean(),
  version: z.string(),
  deployment: z.string(),
});
export type AuthProvidersDTO = z.infer<typeof AuthProvidersDTO>;

export const LoginMethodsInput = z.object({
  email: z.email().max(254),
  workspace: z.string().max(80).optional(),
});
export const LoginMethodsDTO = z.object({
  method: z.enum(['sso', 'password']),
  providerId: z.string().nullable(),
  message: z.string().nullable(),
});
export type LoginMethodsDTO = z.infer<typeof LoginMethodsDTO>;

export const PasswordLoginInput = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(500),
  code: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
  workspace: z.string().max(80).optional(),
});
export type PasswordLoginInput = z.infer<typeof PasswordLoginInput>;

/**
 * A workspace of the signed-in person's organization where they are an active member (same email):
 * the workspace switcher of the rail. `current` marks the workspace of this session.
 */
export const MemberWorkspaceDTO = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  environment: z.string(),
  roleLabel: z.string(),
  current: z.boolean(),
});
export type MemberWorkspaceDTO = z.infer<typeof MemberWorkspaceDTO>;

/**
 * Accept an invite with a local account (docs/SCOPE-v1.md: sign-in = email + password, optional
 * TOTP). When the workspace requires two-factor, the first call answers `unprocessable` with
 * `details.reason = 'totp_setup'`, a generated `totpSecret` and its `otpauthUrl`; the client sends
 * the secret back together with a current `code`. Without the requirement a secret + code turns
 * two-factor on voluntarily.
 */
export const AcceptInviteInput = z
  .object({
    password: z.string().min(1).max(500),
    totpSecret: z
      .string()
      .regex(/^[A-Z2-7]{16,64}$/)
      .optional(),
    code: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
  })
  .refine((v) => (v.totpSecret === undefined) === (v.code === undefined), {
    message: 'Send the two-factor secret together with a code',
    path: ['code'],
  });
export type AcceptInviteInput = z.infer<typeof AcceptInviteInput>;
